/**
 * At most one callback per rendered frame (docs/PERFORMANCE.md).
 *
 * A pointer drag can deliver several moves between two frames, and a high-poll mouse always
 * does. The sim mutation still runs on every event — nothing is dropped — but the repaint
 * that follows is worth doing once. `request` keeps the newest callback and discards the
 * ones it superseded.
 *
 * Phaser-free: the caller supplies the scheduler. The editor schedules on the game's
 * `POST_STEP` so the paint lands in the same frame Phaser is about to render, rather than a
 * `requestAnimationFrame` that would queue behind the game loop and show up one frame late.
 */
export type FrameScheduler = {
  /** Run `fn` once before the next rendered frame. Returns a function that cancels it. */
  schedule: (fn: () => void) => () => void;
};

export class FrameCoalescer {
  private readonly scheduler: FrameScheduler;
  private unschedule: (() => void) | null = null;
  private pending: (() => void) | null = null;

  constructor(scheduler: FrameScheduler) {
    this.scheduler = scheduler;
  }

  get isPending(): boolean {
    return this.pending !== null;
  }

  /** Queue `fn` for the next frame, replacing anything already queued. */
  request(fn: () => void): void {
    this.pending = fn;

    if (this.unschedule) {
      return;
    }

    this.unschedule = this.scheduler.schedule(() => {
      this.unschedule = null;
      this.run();
    });
  }

  /** Run the queued callback now. No-op when nothing is queued. */
  flush(): void {
    if (this.unschedule) {
      this.unschedule();
      this.unschedule = null;
    }

    this.run();
  }

  /** Drop the queued callback without running it. */
  cancel(): void {
    if (this.unschedule) {
      this.unschedule();
      this.unschedule = null;
    }

    this.pending = null;
  }

  private run(): void {
    const fn = this.pending;
    this.pending = null;
    fn?.();
  }
}
